import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import FastImage from '@d11/react-native-fast-image';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import {
  fs,
  s,
  v,
  iconSize,
  borderWidth,
  replaceCurrencyString,
  forceHttps,
  formatNumberDecimal,
  formatMoney,
} from '../../../utils/libs';
import { AppSnackbar } from '../../../services/AppSnackbar';
import { useAppSelector } from '../../../redux/helper';
import {
  selectQuantityButtons,
  selectShowProductImages,
} from '../../../redux/SettingsSlice';
import { GTFonts, useTheme } from '../../../theme';
import type { DemoProductItem } from '../../../hooks/demo';
import Typography from '../../../components/Reusables/Typography';
import QuantityControls from '../../../components/Reusables/QuantityControls';
import { getProductDisplayName } from '../../../utils/productDisplayName';
import { isVariantProduct } from '../../../utils/variantHelpers';
import { demoSellLimit } from '../../../utils/demoProductAdapter';
import { isStockTracked } from '../../../utils/productKind';
import type { ProductObject } from '../../../redux/productSlice';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';

interface DemoProductCardWebProps {
  product: DemoProductItem;
  quantityInCart: number;
  massInCart: number;
  isRowLayout: boolean;
  currency: string;
  highlightAddButton?: boolean;
  hasVariants?: boolean;
  variantCount?: number;
  variantCartTotal?: number;
  priceRangeText?: string;
  onProductPress?: (product: DemoProductItem) => void;
  onAdd: (product: DemoProductItem) => void;
  onRemove: (product: DemoProductItem) => void;
  onSetQuantity: (product: DemoProductItem, quantity: number) => void;
  onSetMass: (product: DemoProductItem, mass: number) => void;
  /**
   * Fires when a TextInput inside this card gains focus. Parent list uses
   * this to scroll the row above the on-screen keyboard.
   */
  onCardInputFocus?: () => void;
}

const DemoProductCardWeb: React.FC<DemoProductCardWebProps> = ({
  product,
  quantityInCart,
  massInCart,
  currency,
  highlightAddButton = false,
  hasVariants: hasVariantsProp,
  variantCount: variantCountProp,
  variantCartTotal = 0,
  priceRangeText,
  onProductPress,
  onAdd,
  onRemove,
  onSetQuantity,
  onSetMass,
  onCardInputFocus,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const showProductImages = useAppSelector(selectShowProductImages);
  const quantityButtons = useAppSelector(selectQuantityButtons) as any;

  const currencyText = replaceCurrencyString('ksh', String(currency || 'KES'));
  const isUnit = product.bp_product_price_type === 'unit';
  const hasVariants =
    hasVariantsProp ?? isVariantProduct(product as unknown as ProductObject);
  const variantCount =
    variantCountProp ?? product.bp_product_variants?.length ?? 0;

  // Match Product.web QuantityControlButton (+) theme (default: success).
  const controlThemeColors = useMemo(() => {
    type ColorKey = keyof typeof theme.colors;
    const mainKey = ((quantityButtons as any)?.plus?.main ||
      'success') as ColorKey;
    const lightKey = (`${String(mainKey)}Light`) as ColorKey;
    return {
      main: theme.colors[mainKey] ?? theme.colors.success,
      light: theme.colors[lightKey] ?? theme.colors.successLight,
    };
  }, [quantityButtons, theme]);
  // Filled once any variant is in the cart, so the card reads as
  // "added" at a glance; light (as before) when nothing is.
  const variantInCart = variantCartTotal > 0;
  const variantContentColor = variantInCart
    ? theme.colors.textInverse
    : controlThemeColors.main;
  // A service has no limit (Infinity): every stock check below stands down
  const maxAvailable = demoSellLimit(product);
  const hasLowStock =
    (product.bp_minimum_stock_level ?? 0) > 0 &&
    maxAvailable <= (product.bp_minimum_stock_level ?? 0);
  const inStock = maxAvailable > 0;

  const [massText, setMassText] = useState(() =>
    massInCart > 0 ? String(massInCart) : '',
  );
  const [isMassFocused, setIsMassFocused] = useState(false);

  useEffect(() => {
    if (!isUnit) return;
    if (isMassFocused) return;
    setMassText(massInCart > 0 ? String(massInCart) : '');
  }, [isUnit, isMassFocused, massInCart]);

  const handleMassTextChange = useCallback(
    (input: string) => {
      let sanitized = input.replace(',', '.').replace(/[^0-9.]/g, '');
      sanitized = sanitized.replace(/^0+(?=\d)/, '');
      if (sanitized.startsWith('.')) sanitized = `0${sanitized}`;

      if (sanitized.length === 0) {
        setMassText('');
        onSetMass(product, 0);
        return;
      }

      if (!/^\d*\.?\d*$/.test(sanitized)) return;

      setMassText(sanitized);

      // Align with Product.web: skip commit for partial "0." so 0.5 can be typed
      if (sanitized.endsWith('.')) {
        const base = sanitized.slice(0, -1);
        if (base === '' || Number(base) === 0) {
          return;
        }
      }

      const num = Number(sanitized);
      if (!Number.isFinite(num)) return;
      if (num <= 0) return;

      if (num > maxAvailable) {
        AppSnackbar.show(
          `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)} ${product.bp_product_unit_of_mass}`,
          'error',
          { durationMs: 7500 },
        );
      }
      onSetMass(product, Math.min(Math.max(0, num), maxAvailable));
    },
    [maxAvailable, onSetMass, product],
  );

  const handleMassBlur = useCallback(() => {
    setIsMassFocused(false);
    const raw = (massText ?? '').trim();
    if (!raw) {
      onSetMass(product, 0);
      return;
    }

    if (raw.endsWith('.')) {
      const trimmed = raw.slice(0, -1);
      const parsed = Number.parseFloat(trimmed);
      if (Number.isNaN(parsed)) {
        setMassText('');
        onSetMass(product, 0);
        return;
      }
      const clamped = Math.min(parsed, maxAvailable);
      if (parsed > maxAvailable) {
        AppSnackbar.show(
          `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)} ${product.bp_product_unit_of_mass}`,
          'error',
          { durationMs: 7500 },
        );
      }
      setMassText(clamped > 0 ? String(clamped) : '');
      onSetMass(product, clamped);
      return;
    }

    const parsed = Number.parseFloat(raw);
    if (Number.isNaN(parsed)) {
      setMassText('');
      onSetMass(product, 0);
      return;
    }
    const clamped = Math.min(parsed, maxAvailable);
    if (parsed > maxAvailable) {
      AppSnackbar.show(
        `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)} ${product.bp_product_unit_of_mass}`,
        'error',
        { durationMs: 7500 },
      );
    }
    setMassText(clamped > 0 ? String(clamped) : '');
    onSetMass(product, clamped);
  }, [massText, maxAvailable, onSetMass, product]);

  const handleQuantityInput = useCallback(
    (input: string) => {
      const normalized = input.replace(/[^\d]/g, '');
      if (normalized.length === 0) {
        onSetQuantity(product, 0);
        return;
      }
      const parsed = Number.parseInt(normalized, 10);
      if (Number.isNaN(parsed)) return;

      if (parsed > maxAvailable) {
        AppSnackbar.show(
          `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)}`,
          'error',
          { durationMs: 7500 },
        );
      }
      onSetQuantity(product, Math.min(parsed, maxAvailable));
    },
    [maxAvailable, onSetQuantity, product],
  );

  const productName = useMemo(
    () => getProductDisplayName(product),
    [product],
  );

  const handleAddWithStockCheck = useCallback(() => {
    if (hasVariants) {
      onProductPress?.(product);
      return;
    }
    if (!inStock) {
      AppSnackbar.show(`${productName} is out of stock`, 'warning', {
        durationMs: 4500,
      });
      return;
    }
    onAdd(product);
  }, [
    hasVariants,
    inStock,
    onAdd,
    onProductPress,
    product,
    productName,
  ]);

  const priceText = useMemo(() => {
    if (hasVariants && priceRangeText) {
      return priceRangeText;
    }
    const sellingPrice = Number(product.bp_product_selling_price ?? 0);
    if (isUnit) {
      const unit = product.bp_product_unit_of_mass
        ? `/${product.bp_product_unit_of_mass}`
        : '';
      return `${formatMoney(sellingPrice)}${unit}`;
    }
    return formatMoney(sellingPrice);
  }, [
    hasVariants,
    priceRangeText,
    isUnit,
    product.bp_product_selling_price,
    product.bp_product_unit_of_mass,
  ]);

  const renderControls = () => {
    if (hasVariants) {
      return (
        <Pressable
          onPress={() => onProductPress?.(product)}
          style={[
            styles.variantSelectButton,
            {
              backgroundColor: variantInCart
                ? controlThemeColors.main
                : controlThemeColors.light,
            },
          ]}>
          <Typography
            style={[
              styles.variantSelectButtonText,
              { color: variantContentColor },
            ]}
            numberOfLines={1}>
            {variantInCart
              ? isUnit
                ? `${formatNumberDecimal(variantCartTotal)}${product.bp_product_unit_of_mass ? ` ${product.bp_product_unit_of_mass}` : ''}`
                : `${formatNumberDecimal(variantCartTotal)} item${variantCartTotal !== 1 ? 's' : ''}`
              : `${variantCount} Variant${variantCount !== 1 ? 's' : ''}`}
          </Typography>
          <FontAwesome6
            name="chevron-down"
            iconStyle="solid"
            size={iconSize(10)}
            color={variantContentColor}
          />
        </Pressable>
      );
    }
    return (
      <QuantityControls
        priceType={isUnit ? 'unit' : 'item'}
        quantity={quantityInCart}
        mass={massInCart}
        massText={massText}
        unitOfMass={product.bp_product_unit_of_mass}
        onAdd={handleAddWithStockCheck}
        onSubtract={() => onRemove(product)}
        onQuantityChange={handleQuantityInput}
        onMassChange={handleMassTextChange}
        onMassFocus={() => setIsMassFocused(true)}
        onMassBlur={handleMassBlur}
        onInputFocus={onCardInputFocus}
        maxQuantity={maxAvailable}
        useWebLayout={true}
        hideSubtractWhenZero={true}
        showPlusDisabledStyle={!inStock}
      />
    );
  };

  const [hasImageError, setHasImageError] = useState(false);
  useEffect(() => {
    setHasImageError(false);
  }, [product.bp_product_image]);

  return (
    <View style={styles.card}>
      {showProductImages ? (
        <View style={styles.imageContainer}>
          {product.bp_product_image && !hasImageError ? (
            <FastImage
              source={{
                uri: forceHttps(product.bp_product_image),
                priority: FastImage.priority.high,
                cache: FastImage.cacheControl.immutable,
              }}
              style={styles.imageInner}
              resizeMode={FastImage.resizeMode.contain}
              onError={() => setHasImageError(true)}
            />
          ) : (
            <View style={styles.imageMissing}>
              <MaterialCommunityIcons
                name={
                  isStockTracked(product.bp_product_kind)
                    ? 'image-plus'
                    : 'briefcase-outline'
                }
                size={iconSize(28)}
                color={theme.colors.textTertiary}
              />
            </View>
          )}

          {(hasLowStock || maxAvailable <= 0) && (
            <View style={[styles.stockIndicator, styles.stockIndicatorText]}>
              <Typography style={styles.stockCount}>
                {maxAvailable <= 0 ? '0' : maxAvailable}
              </Typography>
            </View>
          )}
        </View>
      ) : null}

      <View style={styles.infoOverlay}>
        <View style={styles.meta}>
          <Typography numberOfLines={2} style={styles.name}>
            {productName}
          </Typography>
          <Typography numberOfLines={1} style={styles.price}>
            {currencyText} {priceText}
          </Typography>
        </View>

        <Pressable
          onPress={(e: any) => e.stopPropagation()}
          style={styles.controls}>
          <View style={styles.controlsSlot}>{renderControls()}</View>
        </Pressable>
      </View>
    </View>
  );
};

export default React.memo(DemoProductCardWeb);

const createStyles = (theme: any) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.sm,
      overflow: 'hidden',
      borderWidth: borderWidth(1),
      borderColor: theme.colors.border,
      boxShadow: `0px ${v(4)}px ${s(12)}px rgba(0, 0, 0, 0.08)`,
      transition: 'all 0.2s ease-out',
    } as any,

    imageContainer: {
      width: '100%',
      aspectRatio: 1.5,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: theme.colors.surfaceSecondary,
    },
    imageInner: {
      width: '100%',
      height: '100%',
    } as any,
    imageMissing: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.surfaceSecondary,
    },

    stockIndicator: {
      position: 'absolute',
      top: v(6),
      right: s(6),
      alignItems: 'center',
      justifyContent: 'center',
    },
    stockIndicatorText: {
      minWidth: s(22),
      height: v(22),
      paddingHorizontal: s(4),
    },
    stockCount: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(11),
      color: theme.colors.error,
    },

    infoOverlay: {
      position: 'relative',
      zIndex: 2,
      backgroundColor: theme.colors.surface,
    },
    meta: {
      paddingHorizontal: s(10),
      paddingTop: v(4),
      paddingBottom: s(4),
    },
    name: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.text,
      marginBottom: v(4),
      minHeight: Math.max(v(40), 36),
      overflow: 'hidden',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    price: {
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(20),
      color: theme.colors.text,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },

    controls: {
      paddingHorizontal: s(10),
      paddingBottom: s(10),
    },
    controlsSlot: {
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      minHeight: v(48),
    },
    variantSelectButton: {
      width: s(130),
      minHeight: v(37),
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(8),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(4),
    },
    variantSelectButtonText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(14),
    },
  });
